import { useSelector } from 'react-redux';
import {
  Button,
  Divider,
  FormLabel,
  Input,
  Stack,
  Textarea,
} from '@chakra-ui/react';
import useLoader from '@hooks/useLoader';
import useOrder from '@hooks/useOrder';
import usePopup from '@hooks/usePopup';
import useUpload from '@hooks/useUpload';
import { addressSelector } from '@stores/address';
import { useRouter } from 'next/router';
import { ChangeEvent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AiOutlineHome } from 'react-icons/ai';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { IoImageOutline } from 'react-icons/io5';
import { calculateDistance } from 'utils/common';
import styles from '../order.module.scss';
import { GoMoveToEnd } from 'react-icons/go';
import ROUTER from '@constants/router';

export default function Address() {
  const { currentAddress } = useSelector(addressSelector);
  const [isNearbyAddressCorrect, setIsNearbyAddressCorrect] = useState(true);
  const {
    order: { address },
    updateOrderInfo,
    updateOrderAddressInfo,
  } = useOrder();
  const router = useRouter();
  const { t } = useTranslation('store');
  const { openAlert, closeAllPopup } = usePopup();
  const { handleFileChange, uploadUrl, isUploading } = useUpload({
    bucketName: 'panther-address-v2',
    defaultUrl: currentAddress?.imageUrl,
  });
  ////
  const handleDetail = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = e.target;

    updateOrderInfo({
      address: {
        ...currentAddress,
        imageUrl: uploadUrl,
        detail: value,
        firstFloorPassword: address?.firstFloorPassword,
      },
    });
  };

  const handle1stPassword = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    updateOrderInfo({
      address: {
        ...currentAddress,
        ...address,
        imageUrl: uploadUrl,
        firstFloorPassword: value,
      },
    });
  };

  const getGps = () => {
    if (!currentAddress) return;
    const { lat, lng } = currentAddress;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const {
          coords: { latitude, longitude },
        } = position;
        const distance = calculateDistance(+lat, +lng, latitude, longitude);
        if (distance > 1) setIsNearbyAddressCorrect(false);
      },
      undefined,
      { enableHighAccuracy: true, maximumAge: 1 },
    );
  };

  const handleNewAddressClick = async () => {
    const result = await openAlert({
      description: (
        <>
          <Trans
            i18nKey="address:new_address_reset_warning"
            components={{
              div: (
                <div className="flex flex-col items-center justify-center" />
              ),
              p1: <p />,
              p2: <p />,
            }}
          />
        </>
      ),
      confirmButtonText: '' + t('common:agree'),
      cancelButtonText: '' + t('common:cancel'),
    });

    if (!result) return;

    closeAllPopup();
    router.replace({ pathname: ROUTER.address });
  };

  useEffect(() => {
    if (currentAddress) {
      updateOrderInfo({ address: currentAddress });
      getGps();
    }
  }, []);
  useEffect(() => {
    updateOrderAddressInfo({ imageUrl: uploadUrl as string });
  }, [uploadUrl]);

  return (
    <section className="my-[18px]">
      <Stack spacing="18px" divider={<Divider />}>
        <div className="flex justify-between items-center flex-col gap-[6px]">
          {!isNearbyAddressCorrect && (
            <div>
              <p className={styles.nearbyAddressCorrect}>
                {t('address:is_address_correct')}
              </p>
            </div>
          )}
          <div className="flex items-center gap-[10px]">
            {(currentAddress?.alias + '').toUpperCase() === 'HOME' ? (
              <AiOutlineHome size="20px" />
            ) : (
              <HiOutlineLocationMarker size="20px" />
            )}
            <span className="font-bold">
              {currentAddress?.alias?.toLowerCase()}
            </span>
          </div>
          <div>
            <p className="font-bold text-lg">{currentAddress?.address}</p>
          </div>
          <div className="w-full text-center">
            {currentAddress?.detail ? (
              <div>
                <p>{currentAddress.tempAddress || currentAddress.detail}</p>
              </div>
            ) : (
              <Textarea
                w="full"
                variant="filled"
                placeholder={'' + t('address_placeholder_text')}
                fontSize="small"
                resize="none"
                onInput={handleDetail}
              />
            )}
          </div>
          {!currentAddress?.imageUrl && (
            <Button
              as="label"
              className="w-full flex items-center justify-center gap-[6px] mt-[6px]"
              isLoading={isUploading}
              size="sm"
              colorScheme={address?.imageUrl ? 'blue' : 'gray'}
            >
              <IoImageOutline fontWeight="800" className="font-bold" />
              <span>
                {address?.imageUrl
                  ? t('address_image_upload_complete')
                  : t('upload_address_image_button')}
              </span>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={handleFileChange}
              />
            </Button>
          )}
          {!currentAddress?.detail && (
            <div>
              <FormLabel className="text-sm text-secondary-500 mt-[12px]">
                {t('panther:entrance_password_prompt')}
              </FormLabel>
              <Input
                variant="filled"
                fontSize="small"
                size="sm"
                onInput={handle1stPassword}
              />
            </div>
          )}
        </div>
      </Stack>
      <div className="flex items-center justify-center text-[12px] mt-[8px]">
        <button
          className=" flex items-center gap-[6px] justify-center text-opacity-70 underline text-success-800 cursor-pointer font-bold"
          onClick={handleNewAddressClick}
        >
          <p>{t('address:new_address_setup_prompt')}</p>
          <GoMoveToEnd />
        </button>
      </div>
    </section>
  );
}
